import React, { useEffect, useState } from 'react';

import { Alert, Button, Divider, Empty, Select, Spin, Typography, message } from 'antd';

import { monitorAPI } from '../../../http/MonitorAPI';
import { FieldCropHistory } from '../../../models/FieldCropHistory';
import { FieldReport } from '../../../models/FieldReport';

import ChartsBlock from '../../reports/components/ChartsBlock';
import DailyDetails from '../../reports/components/DailyDetails';
import EventsList from '../../reports/components/EventsList';
import PhaseTimeline from '../../reports/components/PhaseTimeline';
import PrevYearCompare from '../../reports/components/PrevYearCompare';
import Recommendations from '../../reports/components/Recommendations';
import SummaryTiles from '../../reports/components/SummaryTiles';

const { Title, Text } = Typography;
const { Option } = Select;

interface Props {
    fieldId: number;
    seasons: FieldCropHistory[];
}

const FieldReportTab = (props: Props) => {
    const { fieldId, seasons } = props;
    const [selectedYear, setSelectedYear] = useState<number | undefined>(
        seasons.length > 0 ? seasons[0].season_harvest : undefined,
    );
    const [report, setReport] = useState<FieldReport | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    // Сбросить выбор, когда список сезонов изменился
    useEffect(() => {
        if (seasons.length === 0) {
            setSelectedYear(undefined);
            setReport(null);
        } else if (selectedYear === undefined || !seasons.find((s) => s.season_harvest === selectedYear)) {
            setSelectedYear(seasons[0].season_harvest);
        }
    }, [seasons, selectedYear]);

    const handleBuild = () => {
        if (!selectedYear) return;
        setLoading(true);
        setReport(null);
        setErrorMsg('');
        monitorAPI
            .GetFieldReport(fieldId, selectedYear)
            .then((data) => setReport(data))
            .catch((err) => {
                const msg =
                    err?.response?.data?.message ||
                    'Не удалось построить отчёт за этот сезон.';
                setErrorMsg(msg);
            })
            .finally(() => setLoading(false));
    };

    if (seasons.length === 0) {
        return (
            <Empty
                description={
                    <span>
                        У поля нет ни одного сезона.
                        <br />
                        <Text type="secondary">
                            Сначала добавьте сезон на вкладке «Сезоны».
                        </Text>
                    </span>
                }
            />
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <Select
                    style={{ width: 200 }}
                    value={selectedYear}
                    onChange={(v) => setSelectedYear(v)}
                    placeholder="Сезон"
                >
                    {seasons.map((s) => (
                        <Option key={s.season_harvest} value={s.season_harvest}>
                            {s.season_harvest}
                        </Option>
                    ))}
                </Select>
                <Button type="primary" onClick={handleBuild} loading={loading} disabled={!selectedYear}>
                    Построить отчёт
                </Button>
            </div>

            {loading && <Spin size="large" />}

            {!loading && errorMsg && (
                <Alert type="warning" showIcon message="Не удалось построить отчёт" description={errorMsg} />
            )}

            {!loading && !errorMsg && !report && (
                <Empty
                    description={
                        <span>
                            Выберите сезон и нажмите «Построить отчёт».
                            <br />
                            <Text type="secondary">
                                Отчёт строится за вегетационный период автоматически.
                            </Text>
                        </span>
                    }
                    style={{ marginTop: 32 }}
                />
            )}

            {report && (
                <>
                    <Title level={3}>
                        {report.report.meta.culture}
                        {report.report.meta.sort ? `, сорт ${report.report.meta.sort}` : ''}
                        {report.report.meta.region ? ` — ${report.report.meta.region}` : ''}
                    </Title>
                    <Text type="secondary">
                        Период: {report.report.meta.start_date} — {report.report.meta.end_date}
                    </Text>
                    <Divider />
                    <SummaryTiles summary={report.report.summary} />
                    <PhaseTimeline phases={report.report.phase_timeline} />
                    <ChartsBlock daily={report.report.daily} />
                    <DailyDetails daily={report.report.daily} />
                    <EventsList events={report.report.events} />
                    <Recommendations recommendations={report.report.recommendations} />
                    {report.report.prev_year && (
                        <PrevYearCompare
                            current={report.report.summary}
                            prev={report.report.prev_year}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default FieldReportTab;
